import * as React from "react";
import { useLocation } from "wouter";
import { Pencil, Camera, ClipboardPaste, FolderOpen, Plus } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const MOBILE_BREAKPOINT = 768;

function useBreakpoint(): boolean | undefined {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const update = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    mql.addEventListener("change", update);
    update();
    return () => mql.removeEventListener("change", update);
  }, []);
  return isMobile;
}

interface AddRecipeHubProps {
  open: boolean;
  onClose: () => void;
}

const HUB_OPTIONS = [
  {
    icon: Pencil,
    title: "Type a Recipe",
    description: "Enter a recipe by hand",
    href: "/recipe/new",
    color: "bg-[hsl(17_53%_96%)] text-primary border-primary/20",
    iconColor: "text-primary",
  },
  {
    icon: Camera,
    title: "Scan a Recipe",
    description: "Snap or upload a photo to digitize",
    href: "/recipe/scan",
    color: "bg-[hsl(91_18%_95%)] text-secondary border-secondary/20",
    iconColor: "text-secondary",
  },
  {
    icon: ClipboardPaste,
    title: "Paste Recipe Text",
    description: "Copy from a message, note, or website",
    href: "/recipe/paste",
    color: "bg-[hsl(207_35%_96%)] text-foreground border-[hsl(207_35%_82%)]",
    iconColor: "text-[hsl(207_45%_38%)]",
  },
  {
    icon: FolderOpen,
    title: "Batch Import",
    description: "Import many pages at once",
    href: "/admin/upload",
    color: "bg-[hsl(40_33%_95%)] text-foreground border-border",
    iconColor: "text-muted-foreground",
  },
] as const;

function HubOptions({ onClose }: { onClose: () => void }) {
  const [, navigate] = useLocation();

  return (
    <div className="flex flex-col gap-3 mt-2">
      {HUB_OPTIONS.map((opt) => (
        <button
          key={opt.href}
          onClick={() => {
            onClose();
            navigate(opt.href);
          }}
          className={cn(
            "flex items-center gap-4 w-full px-5 py-4 rounded-2xl border text-left",
            "transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]",
            "hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            opt.color
          )}
        >
          <div className={cn("flex-shrink-0 p-2.5 rounded-xl bg-white/60 shadow-sm", opt.iconColor)}>
            <opt.icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p
              className="font-semibold text-base text-foreground"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {opt.title}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
              {opt.description}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

export function AddRecipeHub({ open, onClose }: AddRecipeHubProps) {
  const isMobile = useBreakpoint();

  if (isMobile === undefined) {
    return null;
  }

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl px-6 pb-8 pt-4 max-h-[85vh] overflow-y-auto"
        >
          <div className="mx-auto w-10 h-1.5 rounded-full bg-border mb-6" />
          <SheetHeader className="text-left mb-1">
            <SheetTitle
              className="text-2xl text-foreground"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Add a Recipe
            </SheetTitle>
            <SheetDescription className="sr-only">
              Choose how you would like to add a recipe.
            </SheetDescription>
          </SheetHeader>
          <HubOptions onClose={onClose} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md rounded-3xl px-8 py-8">
        <DialogHeader className="mb-1">
          <DialogTitle
            className="text-2xl text-foreground"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Add a Recipe
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose how you would like to add a recipe.
          </DialogDescription>
        </DialogHeader>
        <HubOptions onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}

interface AddRecipeFABProps {
  onClick: () => void;
}

export function AddRecipeFAB({ onClick }: AddRecipeFABProps) {
  return (
    <button
      onClick={onClick}
      aria-label="Add a recipe"
      className={cn(
        "fixed bottom-[calc(64px+1.25rem)] right-5 z-40",
        "md:bottom-8 md:right-8",
        "w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg",
        "flex items-center justify-center",
        "transition-all duration-200 hover:scale-110 active:scale-95 hover:shadow-xl",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      )}
    >
      <Plus className="w-7 h-7" strokeWidth={2.5} />
    </button>
  );
}
